import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../stores/store';
import { Box, Typography } from '@mui/material';
import { INSPECTION_STATUSES } from '../../_constants/inspectionConst';
import { useTranslation } from 'react-i18next';
import {
  countTableRows,
  createReportTable,
  getMidIndex,
} from '../../_utils/inspectionUtils';
import ReportDtaImage from '../../_common/Report/ReportDtaImage';
import ResultTable, {
  CategoryResultTable,
} from '../../_common/Report/ResultTable';
import { fetchReport } from '../../stores/report/slice';
import { useNavigate } from 'react-router-dom';
import { INSPEX_PATHNAME } from '../../_constants/Const';
import useErrorModal from '../../hooks/useErrorModal';
import ErrorModal from '../../_common/Modal/ErrorModal';
import { isRejected } from '@reduxjs/toolkit';
import { isGetReport } from '../../_utils/typeGuardUtils';
import BasicInfo from '../../_common/BasicInfo/BasicInfo';
import FooterButton from './components/FooterButton';
import useUpdateStatus from '../../hooks/useUpdateStatus';
import { API_CODE } from '../../_constants/apiConst';
import useResetStore from '../../hooks/useResetStore';

const InputResult: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { resetInspectionStore } = useResetStore();
  const { error, showApiError, showTypeError, hideError } = useErrorModal();
  const updateStatus = useUpdateStatus();
  const { repairRequest } = useSelector(
    (state: RootState) => state.repairRequest,
  );
  const { report } = useSelector((state: RootState) => state.report);

  /**
   * テーブルに表示するデータ
   * reportTable[0] : 左側
   * reportTable[1] : 右側
   */
  const [reportTables, setReportTables] = useState<CategoryResultTable[][]>([]);

  useEffect(() => {
    if (repairRequest) {
      fetchReportData(repairRequest.rmaNo, 0);
    }
  }, [repairRequest?.rmaNo]);

  /** 帳票データ取得 */
  const fetchReportData = async (rmaNo: string, qcCount: number) => {
    const response = await dispatch(fetchReport({ rmaNo, qcCount }));
    if (isRejected(response)) {
      showApiError(response.payload, API_CODE.REPORT, () =>
        navigate(INSPEX_PATHNAME.RMA_SEARCH),
      );
      return;
    }
    if (!isGetReport(response.payload)) {
      showTypeError(API_CODE.REPORT);
      return;
    }

    // テーブルのデータをセット
    const reportTableAll = createReportTable(response.payload, qcCount);
    const tableRowList = countTableRows(reportTableAll);
    const midIndex = getMidIndex(tableRowList);
    setReportTables([
      reportTableAll.slice(0, midIndex),
      reportTableAll.slice(midIndex),
    ]);
  };

  /** 次へボタン押下 */
  const onClickNext = async () => {
    if (!repairRequest) {
      return;
    }
    // ステータス更新
    const isSuccess = await updateStatus(
      INSPECTION_STATUSES.INSPECTION_CONFIRM,
      0,
      showApiError,
    );
    if (isSuccess) {
      navigate(INSPEX_PATHNAME.CONFIRMED_INSPECTION);
    }
  };

  /** 戻るボタン押下 */
  const onClickBack = async () => {
    if (!repairRequest) {
      return;
    }

    //ステータスを戻す
    const isSuccess = await updateStatus(
      INSPECTION_STATUSES.INSPECTION_IN_PROGRESS,
      0,
      showApiError,
    );
    if (isSuccess) {
      resetInspectionStore();
      navigate(INSPEX_PATHNAME.INSPECTION_TOP);
    }
  };

  return (
    <Box>
      <BasicInfo />

      <Box mx="3rem">
        <Box
          id="printArea"
          sx={{
            whiteSpace: 'pre-wrap !important',
            wordWrap: 'break-word !important',
          }}
        >
          <Typography pt="1rem" variant="h3">
            {t('LBL_INSPECTION_INPUT_RESULT')}
          </Typography>
          {reportTables.length > 0 && (
            <Box display="flex" justifyContent="space-between" my="2rem">
              <Box width="50%" mr={'5px'}>
                <ResultTable tableData={reportTables[0]} isPreview={false} />
              </Box>
              <Box width="50%" ml={'5px'}>
                <ResultTable tableData={reportTables[1]} isPreview={false} />
                <ReportDtaImage
                  markers={report.markers}
                  repairRequest={repairRequest}
                />
              </Box>
            </Box>
          )}
        </Box>
        {reportTables.length > 0 && (
          <FooterButton onClickNext={onClickNext} onClickBack={onClickBack} />
        )}
      </Box>

      {error && <ErrorModal message={error} onClose={hideError} />}
    </Box>
  );
};

export default InputResult;
